/* 0.48.30 - The password policy (Settings > User management, backend
 * services/password_policy.py), for checking a Password field as it is
 * typed and wording the message in the page's language.
 *
 * Fetched once per visit. Until it arrives the platform's floor (8
 * characters) is used, so a check never waits on the network. The
 * server checks again whatever the page decided. */
const FLOOR = { min_length: 8, lower: false, upper: false, digit: false, symbol: false };
let policy = null;
let inflight = null;
const listeners = new Set();

export function loadPasswordPolicy() {
  if (policy || inflight) return inflight || Promise.resolve(policy);
  inflight = fetch('/api/auth/password-policy', { credentials: 'include' })
    .then(r => (r.ok ? r.json() : null))
    .then((p) => {
      policy = p && typeof p === 'object' ? { ...FLOOR, ...p } : { ...FLOOR };
      listeners.forEach(fn => { try { fn(policy); } catch { /* ignore */ } });
      return policy;
    })
    .catch(() => { policy = { ...FLOOR }; return policy; })
    .finally(() => { inflight = null; });
  return inflight;
}

export function getPasswordPolicy() { return policy || FLOOR; }

export function subscribePasswordPolicy(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* The requirements the password misses ([] when it is fine). Letters
   of any alphabet count; a symbol is neither a letter, a digit nor a
   space. Same rules as the server. */
export function policyProblems(pw, p = getPasswordPolicy()) {
  const s = String(pw ?? '');
  const out = [];
  const chars = [...s];
  if (chars.length < p.min_length || chars.length > 128) out.push('length');
  if (p.lower && !chars.some(c => /\p{Ll}/u.test(c))) out.push('lower');
  if (p.upper && !chars.some(c => /\p{Lu}/u.test(c))) out.push('upper');
  if (p.digit && !chars.some(c => /\p{Nd}/u.test(c))) out.push('digit');
  if (p.symbol && !chars.some(c => !/[\p{L}\p{N}\s]/u.test(c))) out.push('symbol');
  return out;
}

/* "The password does not meet the requirements. It needs: at least 10
   characters, a capital letter, a digit." Every requirement of the
   policy is listed, so the visitor sees the whole rule at once. */
export function policyMessage(t, p = getPasswordPolicy()) {
  const parts = [t('password.req_length', { n: p.min_length })];
  if (p.lower) parts.push(t('password.req_lower'));
  if (p.upper) parts.push(t('password.req_upper'));
  if (p.digit) parts.push(t('password.req_digit'));
  if (p.symbol) parts.push(t('password.req_symbol'));
  return t('password.weak', { requirements: parts.join(', ') });
}

/* Stand-ins a field's error carries until it is shown, where the
   page's language is known (components/PageContentRenderer.jsx). */
export const POLICY_MESSAGE = '__password_policy__';
export const MATCH_MESSAGE = '__password_match__';

/* For the built-in pages: '' when the password fits the policy, else
   the message in the page's language. Waits for the policy (once). */
export async function weakPasswordText(pw, t) {
  const p = await loadPasswordPolicy();
  return policyProblems(pw, p).length ? policyMessage(t, p) : '';
}

/* The server refused with 'weak_password' (the policy changed while the
   page was open, or a page that does not check it first). */
export function isWeakPasswordError(err) {
  return !!err && err.message === 'weak_password';
}
